import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import * as atoms from '../atoms';
import type { AiChatPanelConfig } from '../atoms';
import type { ChatMode } from '../components/AiChat/types';
import type { AgentContext } from '../types';
import useAccount from './useAccount';
import useSelectedEnv from './useSelectedEnv';

// Apply default env behavior: if the user did not explicitly select any
// environments, fall back to the globally selected page env. Removing the chip
// from the input lands here and reverts to the same default.
const withDefaultEnv = (
  context: AgentContext | undefined,
  selectedEnvId: string | undefined,
): AgentContext | undefined => {
  const explicitEnvIds = context?.environmentIds ?? [];
  if (explicitEnvIds.length > 0) return context;
  if (!selectedEnvId) return context;
  return { ...(context ?? {}), environmentIds: [selectedEnvId] };
};

const useAiChatPanel = () => {
  const [
    { isExpanded, isFullWidth, width, initialConfig, context: rawContext },
    setAiChatPanel,
  ] = useRecoilState(atoms.aiChatPanel);

  const { data: account } = useAccount();
  const selectedEnvId = useSelectedEnv();
  const setSelectedEnvId = useSetRecoilState(atoms.selectedEnvironmentId);
  // Set when we change the page env programmatically (e.g. loading a thread),
  // so the page-env-change effect below does not overwrite the env scope we
  // just applied.
  const skipEnvSyncRef = useRef(false);

  const hasAiPanelAccess = useMemo(
    () => !!account?.ai_features_enabled,
    [account],
  );

  const context = useMemo(
    () => withDefaultEnv(rawContext, selectedEnvId),
    [rawContext, selectedEnvId],
  );

  const openPanel = useCallback(
    (
      config?: AiChatPanelConfig & { context?: AgentContext; mode?: ChatMode },
    ) => {
      if (!config) {
        setAiChatPanel((prev) => ({ ...prev, isExpanded: true }));
        return;
      }
      const { context: ctx, mode, ...rest } = config;
      setAiChatPanel((prev) => ({
        ...prev,
        isExpanded: true,
        initialConfig: rest,
        context: 'context' in config ? ctx : prev.context,
        selectedMode: mode ?? prev.selectedMode,
      }));
    },
    [setAiChatPanel],
  );

  const setContext = useCallback(
    (ctx: AgentContext | undefined) => {
      setAiChatPanel((prev) => ({ ...prev, context: ctx }));
    },
    [setAiChatPanel],
  );

  // Loading a thread aligns both the chat scope and the page env with the
  // thread's environments. The page env follows the thread's first env.
  const applyThreadEnvScope = useCallback(
    (envIds: string[] | undefined) => {
      if (envIds?.length) {
        // Only guard the sync effect when the page env actually changes,
        // otherwise the flag would linger and skip the next real change.
        if (envIds[0] !== selectedEnvId) {
          skipEnvSyncRef.current = true;
          setSelectedEnvId(envIds[0]);
        }
        setAiChatPanel((prev) => ({
          ...prev,
          context: { ...(prev.context ?? {}), environmentIds: envIds },
        }));
      } else {
        setAiChatPanel((prev) => ({ ...prev, context: undefined }));
      }
    },
    [setAiChatPanel, setSelectedEnvId, selectedEnvId],
  );

  const closePanel = useCallback(() => {
    setAiChatPanel((prev) => ({
      ...prev,
      isExpanded: false,
      isFullWidth: false,
    }));
  }, [setAiChatPanel]);

  const setWidth = useCallback(
    (newWidth: number) => {
      setAiChatPanel((prev) => ({ ...prev, width: newWidth }));
    },
    [setAiChatPanel],
  );

  const toggleFullWidth = useCallback(() => {
    setAiChatPanel((prev) => ({
      ...prev,
      isFullWidth: !prev.isFullWidth,
    }));
  }, [setAiChatPanel]);

  const clearInitialConfig = useCallback(() => {
    setAiChatPanel((prev) => ({ ...prev, initialConfig: undefined }));
  }, [setAiChatPanel]);

  const setThreadLoadError = useCallback(
    (error: boolean) => {
      setAiChatPanel((prev) => ({ ...prev, threadLoadError: error }));
    },
    [setAiChatPanel],
  );

  useEffect(() => {
    if (!hasAiPanelAccess && isExpanded) {
      closePanel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAiPanelAccess]);

  // Moving to a new page env resets the chat env scope to exactly that env,
  // regardless of any prior selection — unless the change came from loading a
  // thread, which sets its own (possibly multi-env) scope.
  const prevSelectedEnvIdRef = useRef(selectedEnvId);
  useEffect(() => {
    const prevSelectedEnvId = prevSelectedEnvIdRef.current;
    prevSelectedEnvIdRef.current = selectedEnvId;
    if (!selectedEnvId || prevSelectedEnvId === selectedEnvId) return;
    if (skipEnvSyncRef.current) {
      skipEnvSyncRef.current = false;
      return;
    }
    setAiChatPanel((prev) => ({
      ...prev,
      context: { ...(prev.context ?? {}), environmentIds: [selectedEnvId] },
    }));
  }, [selectedEnvId, setAiChatPanel]);

  return {
    isExpanded,
    isFullWidth,
    width,
    initialConfig,
    context,
    openPanel,
    closePanel,
    setWidth,
    toggleFullWidth,
    clearInitialConfig,
    setContext,
    applyThreadEnvScope,
    setThreadLoadError,
  };
};

export default useAiChatPanel;
